﻿import React, { Fragment, useEffect, useRef } from 'react';

import { GMetrixAccordion, GMetrixAccordionItem } from 'components/GMetrixAccordion/GMetrixAccordion';
import { GMetrixContentTreeview } from 'components/GMetrixContentTreeview/GMetrixContentTreeview';
import { CategoryTree, ContentDeliverable, DeliverableType } from 'apis/TransactionApi';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Container, Row } from 'react-bootstrap';
import { useNotification } from 'app/notifications';
import { clipText, formatTimeString } from 'utils';
import { useSuperState } from 'hooks';

import 'components/FindContent/find-content.scss';

export type FindSKUContentProps = {
    ActionsWrapper?: React.ElementType;
    className?: string;
    contentLoader: () => Promise<{ contentFlatList: ContentDeliverable[]; contentTreeView: any }>;
    ContentWrapper?: React.ElementType;
    onClose?: () => void;
    onSuccess?: (result: { content: ContentDeliverable[] }) => void;
    saveButtonLabel?: string;
    selectedContent?: ContentDeliverable[];
};

// SKU Management does not support Custom Tests, so those filters are omitted here.
const DEFAULT_DELIVERABLE_FILTERS = [
    { Checked: false, DeliverableTypeId: DeliverableType.Test, IsCustomTest: false, Name: 'Practice Tests' },
    { Checked: false, DeliverableTypeId: DeliverableType.Course, IsCustomTest: false, Name: 'Courses' },
    { Checked: false, DeliverableTypeId: DeliverableType.Book, IsCustomTest: false, Name: 'Books' }
];

// Builds the treeview "checked" keys for content that is already selected. Matches on Id +
// DeliverableTypeId + ProductId to align with how a SKU selection can span products.
const buildCheckedIds = (
    selectedList: ContentDeliverable[],
    contentFlatList: ContentDeliverable[]
): string[] => {

    const idList: string[] = [];
    selectedList.forEach(pieceOfContent => {

        const match = contentFlatList.find(
            flatListContent => flatListContent.Id === pieceOfContent.Id &&
                flatListContent.DeliverableTypeId === pieceOfContent.DeliverableTypeId &&
                flatListContent.ProductId === pieceOfContent.ProductId
        );
        if (match) {

            idList.push(match.key);

        }

    });
    return idList;

};

export const FindSKUContent = ({
    ActionsWrapper = Fragment,
    contentLoader,
    ContentWrapper = Fragment,
    onClose,
    onSuccess,
    saveButtonLabel = 'Next',
    selectedContent
}: FindSKUContentProps) => {

    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    const [state, setState] = useSuperState({
        allContent: [] as CategoryTree[],
        certificateList: [] as ContentDeliverable[],
        check: { ids: [] as string[] },
        deliverableFilterList: DEFAULT_DELIVERABLE_FILTERS.map(f => ({ ...f })),
        expand: { ids: [] as string[] },
        flatListAllContent: [] as ContentDeliverable[],
        loadingAllContent: true,
        searchAllTerm: '',
        selected: [] as ContentDeliverable[],
        vendorList: [] as ContentDeliverable[]
    });

    const numCheckedVendors = state.vendorList.filter(v => v.Checked).length;
    const numCheckedCertificates = state.certificateList.filter(c => c.CertificateChecked).length;
    const numCheckedDeliverableFilters = state.deliverableFilterList.filter(d => d.Checked).length;

    const getAndSetCertAndVendorLists = (contentFlatList: ContentDeliverable[]) => {

        const vendorList: ContentDeliverable[] = [];
        const certificatesList: ContentDeliverable[] = [];

        for (let i = 0; i < contentFlatList.length; i++) {

            const item = contentFlatList[i];
            const vendorExists = vendorList.find(v => v.VendorId && v.VendorId === item.VendorId);
            const certExists = certificatesList.find(c => c.CertificationName === item.CertificationName);

            if (item && item.VendorId && !vendorExists) {

                item.Checked = false;
                vendorList.push(item);

            }
            if ((item.CertificationName ?? '').length > 0 && !certExists) {

                item.CertificateChecked = false;
                certificatesList.push(item);

            }

        }

        if (vendorList.length > 0) {

            setState({
                vendorList: vendorList.sort((a, b) => {

                    if (a.VendorName < b.VendorName) return -1;
                    if (a.VendorName > b.VendorName) return 1;
                    return 0;

                })
            });

        }
        if (certificatesList.length > 0) {

            setState({ certificateList: certificatesList });

        }

    };

    const navigateThroughTreeView = (
        filteredTreeView: CategoryTree[],
        checkedItems: any[],
        type: 'vendor' | 'certificate' | 'contentType' | 'searchAll'
    ) => {

        const copy: CategoryTree[] = JSON.parse(JSON.stringify(filteredTreeView));
        const categoryItems = [];
        for (let i = 0; i < copy.length; i++) {

            const category = copy[i];
            const productItems = [];
            for (let y = 0; y < category.items.length; y++) {

                const product = category.items[y];
                const deliverableItems = [];
                for (let x = 0; x < product.items.length; x++) {

                    const deliverableType = product.items[x];
                    const newContentItems = [];
                    for (let w = 0; w < deliverableType.items.length; w++) {

                        const contentItem = deliverableType.items[w];
                        switch (type) {

                            case 'vendor': {

                                const matches = checkedItems.find(v => v.VendorId === contentItem.VendorId);
                                if (matches || contentItem.DeliverableTypeId !== DeliverableType.Course) {

                                    newContentItems.push(contentItem);

                                }
                                break;

                            }
                            case 'certificate': {

                                const matches = checkedItems.find(
                                    c => c.CertificationId === contentItem.CertificationId
                                );
                                if (matches) {

                                    newContentItems.push(contentItem);

                                }
                                break;

                            }
                            case 'contentType':
                                for (let index = 0; index < checkedItems.length; index++) {

                                    const contentType = checkedItems[index];
                                    if (contentType.DeliverableTypeId === contentItem.DeliverableTypeId) {

                                        // Custom tests are excluded from SKU Management entirely.
                                        if (contentItem.IsCustomTest) continue;
                                        newContentItems.push(contentItem);

                                    }

                                }
                                break;
                            case 'searchAll': {

                                const term = state.searchAllTerm.toLowerCase();
                                if (
                                    (contentItem.CategoryName ?? '').toLowerCase().includes(term) ||
                                    (contentItem.ProductName ?? '').toLowerCase().includes(term) ||
                                    (contentItem.Name ?? '').toLowerCase().includes(term) ||
                                    (contentItem.CertificationName ?? '').toLowerCase().includes(term)
                                ) {

                                    newContentItems.push(contentItem);

                                }
                                break;

                            }

                        }

                    }
                    deliverableType.items = newContentItems;
                    if (deliverableType.items.length > 0) {

                        deliverableItems.push(deliverableType);

                    }

                }
                product.items = deliverableItems;
                if (product.items.length > 0) {

                    productItems.push(product);

                }

            }
            category.items = productItems;
            if (category.items.length > 0) {

                categoryItems.push(category);

            }

        }
        return categoryItems;

    };

    // Always hide custom tests, regardless of user filters, since SKU Management does not
    // support them.
    const stripCustomTests = (tree: CategoryTree[]): CategoryTree[] => {

        const copy: CategoryTree[] = JSON.parse(JSON.stringify(tree));
        return copy
            .map(category => {

                category.items = category.items
                    .map(product => {

                        product.items = product.items
                            .map(deliverableType => {

                                deliverableType.items = deliverableType.items.filter(c => !c.IsCustomTest);
                                return deliverableType;

                            })
                            .filter(dt => dt.items.length > 0);
                        return product;

                    })
                    .filter(p => p.items.length > 0);
                return category;

            })
            .filter(c => c.items.length > 0);

    };

    const customFilterFunc = () => {

        const checkedVendors = state.vendorList.filter(v => v.Checked);
        const checkedContentTypes = state.deliverableFilterList.filter(t => t.Checked);
        const checkedCertificates = state.certificateList.filter(c => c.CertificateChecked);
        let filteredTreeView: CategoryTree[] = stripCustomTests(state.allContent);

        if (state.searchAllTerm.length > 0) {

            filteredTreeView = navigateThroughTreeView(filteredTreeView, [], 'searchAll');

        }
        if (checkedVendors.length > 0) {

            filteredTreeView = navigateThroughTreeView(filteredTreeView, checkedVendors, 'vendor');

        }
        if (checkedContentTypes.length > 0) {

            filteredTreeView = navigateThroughTreeView(filteredTreeView, checkedContentTypes, 'contentType');

        }
        if (checkedCertificates.length > 0) {

            filteredTreeView = navigateThroughTreeView(filteredTreeView, checkedCertificates, 'certificate');

        }
        return filteredTreeView;

    };

    const onExpandChange = (newExpandedIds: string[]) => setState({ expand: { ids: newExpandedIds } });

    const onCheckChange = (newCheckedIds: string[], newSelected: ContentDeliverable[]) => {

        // Ignore any custom tests that may slip through.
        const merged = [...state.selected];
        for (let i = 0; i < newSelected.length; i++) {

            const item = newSelected[i];
            if (!item || item.IsCustomTest) continue;
            const exists = merged.find(
                s => s.Id === item.Id &&
                    s.DeliverableTypeId === item.DeliverableTypeId &&
                    s.ProductId === item.ProductId
            );
            if (!exists) {

                merged.push(item);

            }

        }

        // Also drop anything that is no longer in newSelected (deselection).
        const stillSelected = merged.filter(m => newSelected.some(
            n => n && n.Id === m.Id &&
                n.DeliverableTypeId === m.DeliverableTypeId &&
                n.ProductId === m.ProductId
        ));
        setState({ check: { ids: newCheckedIds }, selected: stillSelected });

    };

    const toggleVendor = (index: number, checked: boolean) => {

        const list = [...state.vendorList];
        list[index].Checked = checked;
        setState({ vendorList: list });

    };

    const toggleCertificate = (index: number, checked: boolean) => {

        const list = [...state.certificateList];
        list[index].CertificateChecked = checked;
        setState({ certificateList: list });

    };

    const toggleDeliverableFilter = (index: number, checked: boolean) => {

        const list = [...state.deliverableFilterList];
        list[index].Checked = checked;
        setState({ deliverableFilterList: list });

    };

    const setAlreadySelectedCheckBoxes = (contentFlatList: ContentDeliverable[]) => {

        if (selectedContent && selectedContent.length > 0 && contentFlatList.length > 0) {

            setState({
                check: { ids: buildCheckedIds(selectedContent, contentFlatList) },
                selected: selectedContent
            });

        }

    };

    // Keep the initial-load dependencies stable so the effect only runs once on mount.
    const initialLoadRef = useRef({
        contentLoader,
        getAndSetCertAndVendorLists,
        setAlreadySelectedCheckBoxes,
        setState
    });

    useEffect(() => {

        const {
            contentLoader: loader,
            getAndSetCertAndVendorLists: setCertVendor,
            setAlreadySelectedCheckBoxes: setChecked,
            setState: set
        } = initialLoadRef.current;

        const loadData = async () => {

            try {

                const response = await loader();
                set({
                    allContent: response.contentTreeView as CategoryTree[],
                    flatListAllContent: response.contentFlatList,
                    loadingAllContent: false
                });
                setCertVendor(response.contentFlatList);
                setChecked(response.contentFlatList);

            } catch (error) {

                console.error(error);
                set({ loadingAllContent: false });

            }

        };

        loadData();

    }, []);

    const handleLoadIcon = (deliverable: ContentDeliverable, marginSide: 'l' | 'r' = 'r') => {

        let className = '';
        let deliverableTypeText = '';
        switch (deliverable.DeliverableTypeId) {

            case DeliverableType.Test:
                className = 'fa-light fa-ballot-check text-primary';
                deliverableTypeText = formatMessage({ id: 'test.051b8cd1', defaultMessage: 'Test' });
                break;
            case DeliverableType.Book:
                className = 'fa-light fa-book text-success';
                deliverableTypeText = formatMessage({ id: 'ebook.5d34c24a', defaultMessage: 'EBook' });
                break;
            case DeliverableType.Course:
                className = 'fa-light fa-rectangle-history purple';
                deliverableTypeText = formatMessage({ id: 'course.790646f3', defaultMessage: 'Course' });
                break;
            default:
                break;

        }
        return (
            <i
                className={`m${marginSide}-3 size ${className}`}
                style={{ width: '25px' }}
                title={deliverableTypeText}
            ></i>
        );

    };

    const renderDeliverableInfo = (deliverable: ContentDeliverable) => {

        switch (deliverable.DeliverableTypeId) {

            case DeliverableType.Test:
                return (
                    <>
                        {deliverable.TimeInMinutes ? (
                            <div className={`${deliverable.PassingScore ? 'w-50 mx-3' : 'w-100 d-flex justify-content-end'}`}>
                                <FormattedMessage defaultMessage="Time Limit" id="time_limit.50b63b24" />
                                {' '}
                                -
                                {formatTimeString(deliverable.TimeInMinutes * 60)}
                            </div>
                        ) : null}
                        {deliverable.PassingScore ? (
                            <div className={`${deliverable.TimeInMinutes ? 'w-50 ms-3 me-2' : 'w-100 d-flex justify-content-end'}`}>
                                <FormattedMessage defaultMessage="Passing Score" id="passing_score.5fe03d6f" />
                                {' '}
                                -
                                {deliverable.PassingScore}
                            </div>
                        ) : null}
                    </>
                );
            case DeliverableType.Book:
                return (
                    <>
                        {deliverable.AuthorName ? (
                            <div className={`${deliverable.VendorName ? 'w-50 mx-3' : 'w-100 d-flex justify-content-end'}`}>
                                <FormattedMessage defaultMessage="Author" id="author.349be1d2" />
                                {' '}
                                -
                                {deliverable.AuthorName}
                            </div>
                        ) : null}
                        {deliverable.VendorName && (
                            <div className={`${deliverable.AuthorName ? 'w-50 ms-3 me-2' : 'w-100 d-flex justify-content-end'}`}>
                                <FormattedMessage defaultMessage="Publisher" id="publisher.db0c3c7b" />
                                {' '}
                                -
                                {deliverable.VendorName}
                            </div>
                        )}
                    </>
                );
            case DeliverableType.Course:
                return (
                    <>
                        {deliverable.ContentHours ? (
                            <div className={`${deliverable.VendorName ? 'w-50 mx-3' : 'w-100 d-flex justify-content-end'}`}>
                                <FormattedMessage defaultMessage="Hours" id="hours.6c9ed7eb" />
                                {' '}
                                -
                                {deliverable.ContentHours}
                            </div>
                        ) : null}
                        {deliverable.VendorName ? (
                            <div className={`${deliverable.ContentHours ? 'w-50 ms-3 me-2' : 'w-100 d-flex justify-content-end'}`}>
                                <FormattedMessage defaultMessage="Vendor" id="vendor.b7db4ace" />
                                {' '}
                                -
                                {deliverable.VendorName}
                            </div>
                        ) : null}
                    </>
                );
            default:
                return <></>;

        }

    };

    const ItemRender = (item: any) => {

        console.log('item ', item);

        // Content items from the SKU endpoint may not always populate `text`, so fall back
        // to `Name` (the deliverable's display name) to avoid crashing clipText.
        const displayText = item?.text ?? item?.Name ?? '';
        return (
            <div className="find-content-dialog__container__deliverable ms-2 p-3 m-2">
                <div className="w-50 ms-2 me-5" title={displayText}>
                    {clipText(displayText, 50)}
                </div>
                <div className="w-50 d-flex justify-content-center align-items-center ms-5">
                    {renderDeliverableInfo(item)}
                </div>
            </div>
        );

    };

    const showFilters = () => {

        const filterObject = [];
        if (state.vendorList.length > 1) {

            filterObject.push(
                <GMetrixAccordionItem
                    className="p-3 my-2"
                    expanded={false}
                    key={'vendorListKey'}
                    title={`${formatMessage({ id: 'vendorpublisher.33c41856', defaultMessage: 'Vendor/Publisher' })} ${numCheckedVendors > 0 ? `(${numCheckedVendors})` : ''}`}
                >
                    {state.vendorList.map((vendor, i) => (
                        <div className="find-content-dialog__filter-option" key={i + 'container' + 'vendor'}>
                            <GMetrixCheckInput
                                checked={vendor.Checked}
                                className="find-content-dialog__filter-checkbox"
                                label={vendor.VendorName}
                                onChange={e => toggleVendor(i, e.target.checked)}
                                type="checkbox"
                            />
                        </div>
                    ))}
                </GMetrixAccordionItem>
            );

        }
        if (state.certificateList.length > 1) {

            filterObject.push(
                <GMetrixAccordionItem
                    className="p-3 my-2"
                    expanded={false}
                    key={'certificateListKey'}
                    title={`${formatMessage({ id: 'certificate.6feeda64', defaultMessage: 'Certificate' })} ${numCheckedCertificates > 0 ? `(${numCheckedCertificates})` : ''}`}
                >
                    {state.certificateList.map((certificate, i) => (
                        <div className="find-content-dialog__filter-option" key={i + 'container' + 'certificate'}>
                            <GMetrixCheckInput
                                checked={certificate.CertificateChecked}
                                className="find-content-dialog__filter-checkbox"
                                key={i}
                                label={certificate.CertificationName}
                                onChange={e => toggleCertificate(i, e.target.checked)}
                                type="checkbox"
                            />
                        </div>
                    ))}
                </GMetrixAccordionItem>
            );

        }
        return filterObject;

    };

    const filteredContent = state.loadingAllContent ? [] : customFilterFunc();
    const showSideNav = !state.loadingAllContent;

    const handleSaveClick = () => {

        if (state.selected.length === 0) {

            createToast(formatMessage({ id: 'you_need_to_select_content_in_order_to_c.8ded2214', defaultMessage: 'You need to select content in order to continue' }), 'info');
            return;

        }
        onSuccess?.({ content: state.selected });

    };

    return (
        <>
            <ContentWrapper style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <div className="find-content-dialog border">
                    {showSideNav && (
                        <div className="find-content-dialog__side-nav">
                            <div className="p-3 my-2">
                                <GMetrixInput
                                    label={formatMessage({ id: 'search_all.1404365c', defaultMessage: 'Search All' })}
                                    labelClass="find-content-dialog__text"
                                    onChange={e => setState({ searchAllTerm: e.target.value })}
                                    placeholder={formatMessage({ id: 'search_categories_products_or_certificat.80775fce', defaultMessage: 'Search categories, products, or certifications...' })}
                                    value={state.searchAllTerm}
                                />
                            </div>
                            <GMetrixAccordion key={'panelBar'}>
                                <GMetrixAccordionItem
                                    title={(
                                        <b>
                                            <FormattedMessage defaultMessage="Filter" id="filter.a8046688" />
                                        </b>
                                    )}
                                    className="ps-3 my-2"
                                />
                                {showFilters()}
                                <GMetrixAccordionItem
                                    className="p-3 my-2"
                                    expanded={false}
                                    key={'filters'}
                                    title={`${formatMessage({ id: 'content_type.0f2a7eb2', defaultMessage: 'Content Type' })} ${numCheckedDeliverableFilters > 0 ? `(${numCheckedDeliverableFilters})` : ''}`}
                                >
                                    {state.deliverableFilterList.map((deliverable, i) => (
                                        <div
                                            className="find-content-dialog__filter-option"
                                            key={i + 'container' + 'deliverable'}
                                        >
                                            <GMetrixCheckInput
                                                checked={deliverable.Checked}
                                                className="find-content-dialog__filter-checkbox"
                                                key={i}
                                                label={deliverable.Name}
                                                onChange={e => toggleDeliverableFilter(i, e.target.checked)}
                                                type="checkbox"
                                            />
                                        </div>
                                    ))}
                                </GMetrixAccordionItem>
                            </GMetrixAccordion>
                        </div>
                    )}
                    <Container fluid>
                        <Row className="find-content-dialog__container">
                            {!state.loadingAllContent ? (
                                filteredContent.length > 0 ? (
                                    <GMetrixContentTreeview
                                        checkboxes={true}
                                        checkedIds={state.check.ids}
                                        className="find-content-dialog__tree-view"
                                        data={filteredContent}
                                        expandedIds={state.expand.ids}
                                        flatList={state.flatListAllContent}
                                        onCheckChange={onCheckChange}
                                        onExpandChange={onExpandChange}
                                        renderContent={ItemRender}
                                        renderDeliverableInfo={renderDeliverableInfo}
                                        renderIcon={(item: any) => handleLoadIcon(item, 'r')}
                                    />
                                ) : (
                                    <Col className="find-content-dialog__empty-message">
                                        <FormattedMessage defaultMessage="No content is available to select." id="no_content_is_available_to_select.b3a50ff7" />
                                    </Col>
                                )
                            ) : (
                                <Col className="find-content-dialog__loader">
                                    <GMetrixLoader />
                                </Col>
                            )}
                        </Row>
                    </Container>
                </div>
            </ContentWrapper>
            <ActionsWrapper>
                {onClose && (
                    <GMetrixButton className="mx-2" onClick={onClose} variant="secondary">

                        <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                    </GMetrixButton>
                )}
                <GMetrixButton disabled={state.selected.length === 0} onClick={handleSaveClick} variant="primary">
                    {saveButtonLabel}
                </GMetrixButton>
            </ActionsWrapper>
        </>
    );

};

FindSKUContent.displayName = 'FindSKUContent';
